import { useState, useEffect } from 'react';
import axios from 'axios';
import { authGet, authPost, authDelete } from '../../utils/auth';
import { copyToClipboard } from '../../utils/clipboard';
import useRequireAdmin from '../../hooks/useRequireAdmin';
import { Key, Plus, Copy, Trash2, AlertCircle, CheckCircle2, Eye, EyeOff, ExternalLink, Book } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = '/api';

const APIManagement = () => {
  useRequireAdmin();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [newKeyData, setNewKeyData] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [docs, setDocs] = useState(null);
  
  // Create form state
  const [keyName, setKeyName] = useState('');
  const [permissions, setPermissions] = useState({
    read: true,
    write: false,
    delete: false
  });

  const fetchApiKeys = async () => {
    try {
      const response = await authGet('/admin/api-keys');
      setApiKeys(response.data);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to load API keys');
    }
    setLoading(false);
  };

  const fetchDocs = async () => {
    try {
      const response = await axios.get(`${API}/v1/docs`);
      setDocs(response.data);
    } catch (error) {
      console.error('Error fetching docs:', error);
    }
  };

  useEffect(() => {
    fetchApiKeys();
    fetchDocs();
  }, []);

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }

    const selectedPermissions = Object.entries(permissions)
      .filter(([_, enabled]) => enabled)
      .map(([perm]) => perm);

    if (selectedPermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    try {
      const response = await authPost('/admin/api-keys', {
        name: keyName,
        permissions: selectedPermissions,
      });
      
      setNewKeyData(response.data);
      setCreateDialogOpen(false);
      setNewKeyDialogOpen(true);
      setKeyName('');
      setPermissions({ read: true, write: false, delete: false });
      fetchApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? It will no longer be usable.')) {
      return;
    }

    try {
      await authDelete(`/admin/api-keys/${keyId}`);
      fetchApiKeys();
      toast.success('API key revoked');
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!confirm('Are you sure you want to permanently delete this API key?')) {
      return;
    }

    try {
      await authDelete(`/admin/api-keys/${keyId}/permanent`);
      fetchApiKeys();
      toast.success('API key deleted');
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground mb-2">API Management</h1>
        <p className="text-muted-foreground">
          Manage API keys for external integrations with the Orange Label Business API
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Generate New API Key
        </Button>
        <Button variant="outline" onClick={() => setShowDocs(!showDocs)} className="gap-2">
          <Book className="w-4 h-4" />
          {showDocs ? 'Hide' : 'View'} API Documentation
        </Button>
        <a 
          href={`${API}/v1/docs`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm hover:bg-muted transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          JSON Docs Endpoint
        </a>
      </div>

      {/* API Documentation Panel */}
      {showDocs && docs && (
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">API Documentation</h2>
          
          <div className="space-y-6">
            {/* Authentication */}
            <div>
              <h3 className="font-medium text-foreground mb-2">Authentication</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Include your API key in the <code className="bg-muted px-1 py-0.5 rounded">X-API-Key</code> header for all requests.
              </p>
              <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
                curl -H "X-API-Key: your_api_key" {window.location.origin}/api/v1/businesses
              </pre>
            </div>

            {/* Endpoints */}
            <div>
              <h3 className="font-medium text-foreground mb-2">Endpoints</h3>
              
              {/* Listings Endpoints */}
              {docs.endpoints?.listings && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">📋 All Listings (OrangeLabel + BTCMap)</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.listings).map(([key, endpoint]) => (
                      <div key={`listings-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Analytics Endpoints */}
              {docs.endpoints?.analytics && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">📊 Analytics</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.analytics).map(([key, endpoint]) => (
                      <div key={`analytics-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Business CRUD Endpoints */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-muted-foreground mb-2">🏪 Business CRUD</h4>
                <div className="space-y-2">
                {docs.endpoints?.businesses && Object.entries(docs.endpoints.businesses).map(([key, endpoint]) => (
                  <div key={key} className="bg-muted/50 p-3 rounded">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                        endpoint.method === 'GET' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        endpoint.method === 'POST' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                        endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        endpoint.method === 'PATCH' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {endpoint.method}
                      </span>
                      <code className="text-sm">{endpoint.path}</code>
                    </div>
                    <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                    {endpoint.permissions_required && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Permissions: {endpoint.permissions_required.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
                </div>
              </div>
              
              {/* Events Endpoints */}
              {docs.endpoints?.events && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">📅 Events</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.events).map(([key, endpoint]) => (
                      <div key={`events-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Meetup Groups Endpoints */}
              {docs.endpoints?.meetup_groups && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">👥 Meetup Groups</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.meetup_groups).map(([key, endpoint]) => (
                      <div key={`meetup-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Reference Data Endpoints */}
              {docs.endpoints?.reference && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">📚 Reference Data</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.reference).map(([key, endpoint]) => (
                      <div key={`ref-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Services Endpoints */}
              {docs.endpoints?.services && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">🛠️ Services</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.services).map(([key, endpoint]) => (
                      <div key={`svc-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Search Endpoints */}
              {docs.endpoints?.search && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">🔍 Search</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.search).map(([key, endpoint]) => (
                      <div key={`search-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Scrape Logs Endpoints */}
              {docs.endpoints?.scrape_logs && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">📜 Scrape Logs</h4>
                  <div className="space-y-2">
                    {Object.entries(docs.endpoints.scrape_logs).map(([key, endpoint]) => (
                      <div key={`logs-${key}`} className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {endpoint.method}
                          </span>
                          <code className="text-sm">{endpoint.path}</code>
                        </div>
                        <p className="text-sm text-muted-foreground">{endpoint.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Social Media Section */}
              {docs.endpoints?.social_media && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">📱 Social Media</h4>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded border border-indigo-200 dark:border-indigo-800 mb-3">
                    <p className="text-sm text-indigo-700 dark:text-indigo-400">{docs.endpoints.social_media.note}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Connections:</p>
                      <div className="flex gap-2">
                        <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-1 rounded">NOSTR</span>
                        <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-1 rounded">X (via Make.com)</span>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Templates:</p>
                      <div className="space-y-1">
                        <div className="bg-muted/50 p-2 rounded">
                          <p className="text-sm font-medium">New Listing <span className="text-xs text-green-600">(Auto)</span></p>
                          <p className="text-xs text-muted-foreground">Auto-fires when business approved to OrangeLabel</p>
                        </div>
                        <div className="bg-muted/50 p-2 rounded">
                          <p className="text-sm font-medium">Business Awareness</p>
                          <p className="text-xs text-muted-foreground">Daily 9am-12pm Sydney - random non-ABIB business</p>
                        </div>
                        <div className="bg-muted/50 p-2 rounded">
                          <p className="text-sm font-medium">ABIB Members</p>
                          <p className="text-xs text-muted-foreground">Daily 12pm-3pm Sydney - random ABIB member</p>
                        </div>
                      </div>
                    </div>
                    
                    {docs.endpoints.social_media.api_endpoint && (
                      <div className="bg-muted/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                            POST
                          </span>
                          <code className="text-sm">/api/v1/social-media/new-listing</code>
                        </div>
                        <p className="text-sm text-muted-foreground">Trigger New Listing post via API</p>
                        <p className="text-xs text-muted-foreground mt-1">Body: {"{"}"business_id": "..." or "business_name": "..."{"}"}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Listing Destinations */}
            <div>
              <h3 className="font-medium text-foreground mb-2">Listing Destinations</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded border border-orange-200 dark:border-orange-800">
                  <p className="font-medium text-orange-700 dark:text-orange-400">orangelabel</p>
                  <p className="text-xs text-muted-foreground">Physical location business for Orange Label directory</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                  <p className="font-medium text-blue-700 dark:text-blue-400">btcmap</p>
                  <p className="text-xs text-muted-foreground">Should be added to BTCMap instead</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-900/20 p-3 rounded border border-gray-200 dark:border-gray-800">
                  <p className="font-medium text-gray-700 dark:text-gray-400">pending</p>
                  <p className="text-xs text-muted-foreground">Needs review to determine destination</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="font-semibold">API Keys</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Loading...</div>
        ) : apiKeys.length === 0 ? (
          <div className="p-8 text-center">
            <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No API keys created yet</p>
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Generate Your First API Key
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {apiKeys.map(key => (
              <div key={key.id} className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-foreground">{key.name}</span>
                    {key.is_active ? (
                      <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded">Active</span>
                    ) : (
                      <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-0.5 rounded">Revoked</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Key: <code className="bg-muted px-1 py-0.5 rounded">{key.key_prefix}...</code></p>
                    <p>Permissions: {key.permissions?.join(', ') || 'none'}</p>
                    <p>Created: {formatDate(key.created_at)} by {key.created_by}</p>
                    <p>Last used: {formatDate(key.last_used_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {key.is_active && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleRevokeKey(key.id)}
                      className="text-yellow-600 hover:text-yellow-700"
                    >
                      Revoke
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDeleteKey(key.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate New API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for external integrations. The key will only be shown once.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="keyName">Key Name</Label>
              <Input 
                id="keyName"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g., External Integration, BTCMap Sync"
              />
            </div>
            
            <div>
              <Label>Permissions</Label>
              <div className="space-y-2 mt-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="perm-read" 
                    checked={permissions.read}
                    onCheckedChange={(checked) => setPermissions({...permissions, read: checked})}
                  />
                  <label htmlFor="perm-read" className="text-sm">
                    <span className="font-medium">Read</span> - View businesses and data
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="perm-write" 
                    checked={permissions.write}
                    onCheckedChange={(checked) => setPermissions({...permissions, write: checked})}
                  />
                  <label htmlFor="perm-write" className="text-sm">
                    <span className="font-medium">Write</span> - Create and update businesses
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="perm-delete" 
                    checked={permissions.delete}
                    onCheckedChange={(checked) => setPermissions({...permissions, delete: checked})}
                  />
                  <label htmlFor="perm-delete" className="text-sm">
                    <span className="font-medium">Delete</span> - Remove businesses
                  </label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateKey}>Generate Key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Created Dialog */}
      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              Copy your API key now. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          
          {newKeyData && (
            <div className="space-y-4 py-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    Save this key securely. It will not be displayed again.
                  </p>
                </div>
              </div>
              
              <div>
                <Label>Your API Key</Label>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={newKeyData.key} 
                    readOnly 
                    className="font-mono text-sm"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => copyToClipboard(newKeyData.key)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p><strong>Name:</strong> {newKeyData.name}</p>
                <p><strong>Permissions:</strong> {newKeyData.permissions?.join(', ')}</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setNewKeyDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default APIManagement;
